/**
 * 插槽系统注册表 (Slot Registry)
 *
 * 这个模块提供了一个插槽系统，用于在文章内容中识别和渲染特定的组件标记。
 * 支持在标记中处理末尾空格，以增强兼容性。
 *
 * ============================================================================
 * 匹配情况说明
 * ============================================================================
 *
 * ✅ 能匹配到的情况：
 * ────────────────────────────────────────────────────────────────────────
 *  - '<code>🔧 内容监测</code>'           ✅ 完全匹配
 *  - '<code>🔧 内容监测 </code>'          ✅ </code> 前有1个空格
 *  - '<code>🔧 内容监测  </code>'         ✅ </code> 前有多个空格
 *  - '<code>🔧 内容监测\t</code>'         ✅ </code> 前有 tab 字符
 *  - '<code>🔧 内容监测\n</code>'         ✅ </code> 前有换行符
 *  - '<code>🔧 反向搜索</code>'           ✅ 另一个完整标记
 *  - '<code>🔧 反向搜索  </code>'         ✅ 另一个标记带空格
 *
 * ❌ 不能匹配到的情况：
 * ────────────────────────────────────────────────────────────────────────
 *  - ' <code>🔧 内容监测</code>'          ❌ 开头有空格
 *  - '<code >🔧 内容监测</code>'          ❌ code 和 > 之间有空格
 *  - '<code>🔧  内容监测</code>'          ❌ 内容中间多个空格
 *  - '<CODE>🔧 内容监测</CODE>'           ❌ 大小写不同
 *  - '<code>🔧 内容监测</code> '          ❌ 末尾有空格（超出 </code>）
 *
 * ============================================================================
 */

import React from 'react';
import ReverseSearch from './reverse-search';
import AddAccountSlot from './add-account-slot';

// 插槽组件映射表
export const slotComponents: Record<string, React.ComponentType<any>> = {
  '<code>🔧 内容监测</code>': AddAccountSlot,
  '<code>🔧 反向搜索</code>': ReverseSearch,
};

// 获取所有插槽标记的正则表达式（处理 </code> 前面可能存在的空格）
export const getSlotPattern = (): RegExp => {
  const patterns = Object.keys(slotComponents).map((key) => {
    // 先转义所有特殊正则字符（包括 < 和 >）
    const escaped = key.replace(/[.*+?^${}()|[\]\\<>]/g, '\\$&');
    // 然后将转义后的 \< 替换为 \s*<，允许前面有空格
    const pattern = escaped.replace(/\\\</g, '\\s*<');
    return pattern;
  });
  return new RegExp(`(${patterns.join('|')})`, 'g');
};

// 根据标记获取对应的组件（处理空格问题）
export const getSlotComponent = (marker: string): React.ComponentType<any> | null => {
  // 先直接查找
  if (slotComponents[marker]) {
    return slotComponents[marker];
  }

  // 如果直接查找不到，尝试通过规范化空格来查找
  // 移除所有 < 前面的空格
  const normalizedMarker = marker.replace(/\s+</g, '<');
  for (const [key] of Object.entries(slotComponents)) {
    if (normalizedMarker === key) {
      return slotComponents[key];
    }
  }

  return null;
};
